import React, { useState } from "react";
import axios from "axios";
import { LockIcon } from "./Icons";

/* Local icons */
const HardHatIcon = ({ size = 32, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2z"/>
    <path d="M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5"/>
    <path d="M4 15v-3a8 8 0 0 1 16 0v3"/>
  </svg>
);
const AlertIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const EyeIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const StaffFirstLoginModal = ({ user, setUser }) => {
  const [form, setForm] = useState({ name: user?.name || "", newPassword: "", confirmPassword: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Please enter your name."); return; }
    if (form.newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (form.newPassword !== form.confirmPassword) { setError("Passwords do not match."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.put(
        "/api/auth/me/first-login",
        { name: form.name, newPassword: form.newPassword },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      if (res.data.success) {
        // Persist updated user (name, isFirstLogin: false, new token)
        const updated = { ...res.data };
        localStorage.setItem("user", JSON.stringify(updated));
        setUser(updated);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md p-8 border border-slate-700">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-3xl mx-auto mb-5 flex items-center justify-center shadow-xl">
            <HardHatIcon size={36} color="white" />
          </div>
          <h2 className="text-2xl font-black text-white mb-2">Welcome to the Team!</h2>
          <p className="text-slate-400 text-sm">Before you continue, please set up your name and a new password for your staff account.</p>
          <div className="inline-flex items-center gap-2 mt-3 px-4 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
            <LockIcon size={12} color="#92400e" /> First-Time Setup Required
          </div>
        </div>

        {error && (
          <div className="mb-5 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-400 text-sm font-bold flex items-center gap-2">
            <AlertIcon size={16} color="#f87171" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
              Your Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setError(""); }}
              className="w-full px-4 py-3.5 border border-slate-700 rounded-2xl bg-slate-800 text-white text-sm font-medium placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all"
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* New Password */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                value={form.newPassword}
                onChange={e => { setForm(f => ({ ...f, newPassword: e.target.value })); setError(""); }}
                className="w-full px-4 py-3.5 pr-12 border border-slate-700 rounded-2xl bg-slate-800 text-white text-sm font-medium placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all"
                placeholder="At least 6 characters"
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd(s => !s)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
              >
                {showPwd ? <EyeOffIcon size={18} color="currentColor" /> : <EyeIcon size={18} color="currentColor" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
              Confirm Password
            </label>
            <input
              type={showPwd ? "text" : "password"}
              value={form.confirmPassword}
              onChange={e => { setForm(f => ({ ...f, confirmPassword: e.target.value })); setError(""); }}
              className="w-full px-4 py-3.5 border border-slate-700 rounded-2xl bg-slate-800 text-white text-sm font-medium placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all"
              placeholder="Re-enter your password"
              required
            />
          </div>

          {/* Password match indicator */}
          {form.confirmPassword && (
            <p className={`text-xs font-bold ${form.newPassword === form.confirmPassword ? "text-emerald-400" : "text-red-400"}`}>
              {form.newPassword === form.confirmPassword ? "✓ Passwords match" : "✗ Passwords do not match"}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-black text-base rounded-2xl shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          >
            {loading ? "Saving…" : "Complete Setup & Continue →"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StaffFirstLoginModal;
